import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TagsInput } from "@/components/TagsInput";
import { LeadTimeline } from "@/components/LeadTimeline";
import { LossReasonModal } from "@/components/LossReasonModal";
import { TasksBlock } from "@/components/TasksBlock";
import TimePresetSelect from "@/components/TimePresetSelect";
import RdvSyncBadge from "@/components/rdv/RdvSyncBadge";
import RdvDebriefBlock from "@/components/rdv/RdvDebriefBlock";
import {
  formatTimeSlot,
  formatDateAndTimeSlot,
  isoUtcToBrusselsLocal,
  brusselsLocalToIsoUtc,
} from "@/utils/formatRdvTimeSlot";
import {
  Loader2,
  ChevronLeft,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Hammer,
  Plus,
  ChevronDown,
  ChevronRight,
  Calendar,
  Download,
  Trash2,
  Pencil,
  Link2,
  RefreshCw,
  Copy,
  Check,
  Send,
  FileText,
} from "lucide-react";

const STATUSES = [
  ["en_attente", "En attente"],
  ["contacte", "Contacté"],
  ["rdv_fixe", "RDV fixé"],
  ["devis_envoye", "Devis envoyé"],
  ["en_negociation", "En négociation"],
  ["signe", "Signé"],
  ["perdu", "Perdu"],
];

const formatDate = (iso) => new Date(iso).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" });
// Version compacte sans heure pour les badges (évite le "21:15" flottant sur mobile)
const formatDateShort = (iso) => new Date(iso).toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" });

// --- Split datetime-local en date + time --------------------------------
// Le picker natif `datetime-local` est catastrophique sur iPad Safari
// (overlay latéral qui bloque tous les autres champs du formulaire).
// On expose 2 <input> distincts (type="date" + type="time") reliés à
// `rdvForm.scheduled_at` (format "YYYY-MM-DDTHH:mm" **interprété
// Europe/Brussels**, pas UTC). La conversion UTC↔Bruxelles est déléguée aux
// helpers `isoUtcToBrusselsLocal` / `brusselsLocalToIsoUtc` du module
// `formatRdvTimeSlot` — jamais `toISOString().slice(0,16)` (bug tz -2h).
const splitDateTime = (localStr) => {
  if (!localStr) return { date: "", time: "" };
  const [d, t] = localStr.split("T");
  return { date: d || "", time: (t || "").slice(0, 5) };
};
const joinDateTime = (date, time) => (date && time ? `${date}T${time}` : "");

// Phase 2C.M+ (2026-09-06) — labels statuts devis pour bloc compact
// colonne droite. Aligné sur ChantierPage.jsx.
const QUOTE_STATUS_LABELS = {
  brouillon: { label: "Brouillon", cls: "badge-neutral" },
  envoye: { label: "Envoyé", cls: "badge-warning" },
  signe: { label: "Signé", cls: "badge-success" },
  refuse: { label: "Refusé", cls: "badge-error" },
  expire: { label: "Expiré", cls: "badge-neutral" },
};
const fmtEur = (n) => (typeof n === "number" ? n.toFixed(2).replace(".", ",") + " €" : "—");

export default function ProjectDetailPage() {
  const params = useParams();
  const leadId = params.projectId || params.leadId;
  const nav = useNavigate();
  const { state: authState } = useAuth();
  const currentUserId = authState?.user?.id || "";
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [sources, setSources] = useState([]);
  const [note, setNote] = useState("");
  const [showPayload, setShowPayload] = useState(false);
  const [chantierOpen, setChantierOpen] = useState(false);
  const [chantierForm, setChantierForm] = useState({ name: "", chantier_type_id: "" });
  const [chantierTypes, setChantierTypes] = useState([]);
  const [showLossModal, setShowLossModal] = useState(false);
  const [rendezvous, setRendezvous] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [rdvOpen, setRdvOpen] = useState(false);
  const [rdvEditing, setRdvEditing] = useState(null); // rdv object or null
  const [hubModal, setHubModal] = useState(null); // null | 'view' | 'regenerate'
  const [hubInfo, setHubInfo] = useState(null); // {hub_url, hub_token}
  const [hubResendTo, setHubResendTo] = useState("");
  const [hubCopied, setHubCopied] = useState(false);
  const [hubBusy, setHubBusy] = useState(false);
  const [rdvForm, setRdvForm] = useState({
    scheduled_at: "",
    duration_minutes: 60,
    contact_name: "",
    location: "",
    notes: "",
    reminder_minutes_before: 60,
    assigned_user_id: "",
  });
  const [savingRdv, setSavingRdv] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, mem, srcs, ct, rdvs, qs] = await Promise.all([
        api.get(`/leads/${leadId}`),
        api.get("/team/members").catch(() => ({ data: [] })),
        api.get("/lead-sources"),
        api.get("/chantier-types").catch(() => ({ data: [] })),
        api.get(`/rendezvous?lead_id=${leadId}`).catch(() => ({ data: [] })),
        api.get(`/quotes?lead_id=${leadId}`).catch(() => ({ data: { items: [] } })),
      ]);
      setData(d.data);
      setMembers(mem.data);
      setSources(srcs.data);
      setChantierTypes(ct.data);
      setRendezvous(rdvs.data || []);
      // /api/quotes retourne {items, total} — extraction résiliente
      setQuotes(Array.isArray(qs.data) ? qs.data : (qs.data?.items || []));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load();   }, [leadId]);

  // Turn 2b UX-C : auto-ouverture du modal RDV via `?open_rdv=1` (depuis Kanban ou fiche contact).
  useEffect(() => {
    if (loading || !data) return;
    if (searchParams.get("open_rdv") === "1" && !rdvOpen) {
      setRdvEditing(null);
      setRdvForm({
        scheduled_at: "",
        duration_minutes: 60,
        contact_name: `${data.lead.first_name || ""} ${data.lead.last_name || ""}`.trim(),
        location: [data.lead.address_street, data.lead.address_zip, data.lead.address_city].filter(Boolean).join(" "),
        notes: "",
        reminder_minutes_before: 60,
        // Phase A hotfix Item B : assigné par défaut = créateur
        assigned_user_id: data.lead.assigned_to_user_id || currentUserId || "",
      });
      setRdvOpen(true);
      // Consomme le query param pour éviter la ré-ouverture au refresh
      const next = new URLSearchParams(searchParams);
      next.delete("open_rdv");
      setSearchParams(next, { replace: true });
    }
  }, [loading, data, searchParams, rdvOpen, setSearchParams]);

  // Fix B.3 (2026-09-09) — Consommation silencieuse de `?open_task=1`.
  // Le débrief RDV avec follow_up_action="task" navigue vers
  // /projects/{id}?open_task=1. À ce jour, aucune modale "Créer une tâche"
  // n'est câblée sur cette page (le bloc `TasksBlock` a son propre flux
  // interne). Sans consommation, le paramètre reste dans l'URL sans effet
  // visible (bug UX signalé). Fix minimal validé user (option a) :
  // nettoyage silencieux + `console.info` pour debug futur, aucune UI
  // nouvelle introduite. Quand une modale dédiée sera ajoutée, cet
  // effet devra la déclencher avant de nettoyer.
  useEffect(() => {
    if (loading || !data) return;
    if (searchParams.get("open_task") !== "1") return;
    console.info(
      "[ProjectDetailPage] ?open_task=1 consommé silencieusement (fix B.3 2026-09-09) — aucune modale tâche câblée à ce jour.",
    );
    const next = new URLSearchParams(searchParams);
    next.delete("open_task");
    setSearchParams(next, { replace: true });
  }, [loading, data, searchParams, setSearchParams]);

  if (loading || !data) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const { lead, interactions, chantiers } = data;

  const patch = async (updates) => {
    try {
      await api.patch(`/leads/${leadId}`, updates);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/leads/${leadId}/notes`, { content: note.trim() });
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createChantier = async () => {
    if (!chantierForm.name.trim()) return;
    try {
      const payload = { lead_id: leadId, name: chantierForm.name.trim() };
      if (chantierForm.chantier_type_id) payload.chantier_type_id = chantierForm.chantier_type_id;
      await api.post("/chantiers", payload);
      toast.success("Chantier créé");
      setChantierOpen(false);
      setChantierForm({ name: "", chantier_type_id: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const memberName = (id) => {
    const m = members.find(x => x.id === id);
    return m ? `${m.first_name} ${m.last_name}` : "Non assigné";
  };
  const src = sources.find(s => s.id === lead.source_id);
  const sourceName = src?.name || "—";
  const sourceArchived = src ? !src.active : false;

  const openNewRdv = () => {
    setRdvEditing(null);
    setRdvForm({
      scheduled_at: "",
      duration_minutes: 60,
      contact_name: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(),
      location: [lead.address_street, lead.address_zip, lead.address_city].filter(Boolean).join(" "),
      notes: "",
      reminder_minutes_before: 60,
      // Phase A hotfix Item B : assigné par défaut au créateur (user courant),
      // pas "Non assigné". Fallback assigné du lead si présent.
      assigned_user_id: lead.assigned_to_user_id || currentUserId || "",
    });
    setRdvOpen(true);
  };

  const openEditRdv = (rdv) => {
    setRdvEditing(rdv);
    setRdvForm({
      // Phase A hotfix P0 timezone : préremplir en heure Bruxelles, PAS UTC.
      // Le legacy `toISOString().slice(0,16)` renvoyait l'heure UTC → décalage
      // -2h (été) / -1h (hiver) silencieux dans l'input `type="time"`.
      scheduled_at: rdv.scheduled_at ? isoUtcToBrusselsLocal(rdv.scheduled_at) : "",
      duration_minutes: rdv.duration_minutes,
      contact_name: rdv.contact_name || "",
      location: rdv.location || "",
      notes: rdv.notes || "",
      reminder_minutes_before: rdv.reminder_minutes_before,
      assigned_user_id: rdv.assigned_user_id || "",
    });
    setRdvOpen(true);
  };

  const submitRdv = async () => {
    if (!rdvForm.scheduled_at) {
      toast.error("Date et heure requises");
      return;
    }
    setSavingRdv(true);
    try {
      const body = {
        lead_id: leadId,
        // Phase A hotfix P0 timezone : convertir explicitement l'input local
        // Bruxelles → UTC via `brusselsLocalToIsoUtc` (gère DST). Le legacy
        // `new Date(local).toISOString()` dépendait de la tz du navigateur
        // (bug voyage : safe sur browser Brussels mais faux à l'étranger).
        scheduled_at: brusselsLocalToIsoUtc(rdvForm.scheduled_at),
        duration_minutes: Number(rdvForm.duration_minutes),
        contact_name: rdvForm.contact_name,
        location: rdvForm.location,
        notes: rdvForm.notes,
        reminder_minutes_before: Number(rdvForm.reminder_minutes_before),
        assigned_user_id: rdvForm.assigned_user_id || null,
      };
      if (rdvEditing) {
        await api.patch(`/rendezvous/${rdvEditing.id}`, body);
        toast.success("RDV modifié — email et .ICS mis à jour envoyés (mode test si applicable)");
      } else {
        await api.post("/rendezvous", body);
        toast.success("RDV créé — email et .ICS envoyés au client (mode test si applicable)");
      }
      setRdvOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingRdv(false);
    }
  };

  const cancelRdv = async (rdv) => {
    if (!window.confirm(`Annuler le RDV du ${formatDate(rdv.scheduled_at)} ?`)) return;
    try {
      await api.delete(`/rendezvous/${rdv.id}`);
      toast.success("RDV annulé — email d'annulation envoyé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Phase 2C.M+ (2026-09-06) — Création devis depuis fiche projet.
  // Le backend accepte `lead_id` seul (sans chantier_id) : le chantier
  // est créé automatiquement à la signature du devis (Phase 2A F.2.a).
  const createQuote = async () => {
    setCreatingQuote(true);
    try {
      const { data: q } = await api.post("/quotes", {
        lead_id: leadId,
        lines: [],
        notes: "",
      });
      toast.success("Brouillon de devis créé");
      nav(`/quotes/${q.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreatingQuote(false);
    }
  };

  const downloadIcs = async (rdv) => {
    try {
      const resp = await api.get(`/rendezvous/${rdv.id}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rdv-${rdv.id.slice(0, 8)}.ics`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5 pb-8" data-testid="lead-detail-page">
      <div className="flex items-center justify-between gap-3">
        <Link to="/pipeline" className="inline-flex items-center gap-1 text-sm text-[#E07A62] hover:underline">
          <ChevronLeft className="h-4 w-4" /> Retour au pipeline
        </Link>
        {lead.contact_id && (
          <Link
            to={`/contacts/${lead.contact_id}`}
            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800/50 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700"
            data-testid="view-contact-link"
          >
            Voir le contact →
          </Link>
        )}
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" data-testid="lead-name">
            {lead.first_name} {lead.last_name}
            {(lead.resubmission_count || 1) > 1 && (
              <Badge
                variant="outline"
                className="badge-warning ml-3 align-middle text-xs"
                data-testid="lead-resubmission-badge"
                title={`Ce lead a été soumis ${lead.resubmission_count} fois via webhook. Voir "Historique des payloads" en bas.`}
              >
                {lead.resubmission_count}× soumissions
              </Badge>
            )}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-400">
            {lead.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{lead.phone}</span>}
            {lead.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{lead.email}</span>}
            {lead.address_city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{lead.address_city}</span>}
          </div>
          <div className="mt-2 flex gap-2 text-xs">
            <Badge variant="outline" className="border-slate-600 bg-slate-800 text-slate-300" data-testid="lead-source-badge">
              Source : {sourceName}
            </Badge>
            {sourceArchived && (
              <Badge variant="outline" className="border-slate-700 bg-slate-800/50 text-slate-500" data-testid="lead-source-archived-badge">
                Archivée
              </Badge>
            )}
            <Badge variant="outline" className="border-slate-600 bg-slate-800 text-slate-300">Créé le {formatDateShort(lead.created_at)}</Badge>
            {lead.marketing_consent?.granted && (
              <Badge
                variant="outline"
                className="border-[#7A8450]/60 bg-[#7A8450]/10 text-[#B8C784]"
                data-testid="lead-marketing-consent-badge"
                title={`Consentement marketing donné le ${formatDate(lead.marketing_consent.granted_at)}`}
              >
                Consentement marketing ✓
              </Badge>
            )}
          </div>
          <div className="mt-3">
            <Label className="text-xs text-slate-500">Tags</Label>
            <div className="mt-1">
              <TagsInput
                value={lead.tags || []}
                onChange={(tags) => patch({ tags })}
                placeholder="Ajouter un tag…"
              />
            </div>
          </div>
          <div className="mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const { data } = await api.get(`/leads/${leadId}/hub`);
                  setHubInfo(data);
                  setHubResendTo(lead.email || "");
                  setHubModal("view");
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              className="border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-700"
              data-testid="hub-open-btn"
            >
              <Link2 className="mr-2 h-3.5 w-3.5" /> Espace client
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-2 min-w-[200px]">
          <div>
            <Label className="text-xs text-slate-500">Statut</Label>
            <Select
              value={lead.status}
              onValueChange={(v) => {
                if (v === "perdu" && lead.status !== "perdu") {
                  setShowLossModal(true);
                } else {
                  patch({ status: v });
                }
              }}
            >
              <SelectTrigger className="border-slate-700 bg-slate-950/40" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                {STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Commercial du projet</Label>
            <Select
              value={lead.assigned_to_user_id || "none"}
              onValueChange={(v) => patch({ assigned_to_user_id: v === "none" ? null : v })}
            >
              <SelectTrigger className="border-slate-700 bg-slate-950/40"><SelectValue /></SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                <SelectItem value="none">Non assigné</SelectItem>
                {members.map(m => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card className="border-slate-800 bg-slate-900/40">
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-200">Coordonnées</h3>
                {lead.contact_id && (
                  <Link
                    to={`/contacts/${lead.contact_id}`}
                    className="inline-flex items-center gap-1 text-xs text-[#E07A62] hover:underline"
                    data-testid="edit-on-contact-link"
                  >
                    <Pencil className="h-3 w-3" /> Éditer sur la fiche contact
                  </Link>
                )}
              </div>
              {lead.contact_id ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="block text-[10px] uppercase tracking-wide text-slate-500">Prénom</span>
                    <span className="text-slate-200">{lead.first_name || <span className="text-slate-600">—</span>}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wide text-slate-500">Nom</span>
                    <span className="text-slate-200">{lead.last_name || <span className="text-slate-600">—</span>}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wide text-slate-500">Téléphone</span>
                    <span className="text-slate-200">{lead.phone || <span className="text-slate-600">—</span>}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wide text-slate-500">Email</span>
                    <span className="break-all text-slate-200">{lead.email || <span className="text-slate-600">—</span>}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="block text-[10px] uppercase tracking-wide text-slate-500">Rue et n°</span>
                    <span className="text-slate-200">{lead.address_street || <span className="text-slate-600">—</span>}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wide text-slate-500">CP</span>
                    <span className="text-slate-200">{lead.address_zip || <span className="text-slate-600">—</span>}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wide text-slate-500">Ville</span>
                    <span className="text-slate-200">{lead.address_city || <span className="text-slate-600">—</span>}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="block text-[10px] uppercase tracking-wide text-slate-500">N° TVA</span>
                    <span className="text-slate-200">{lead.vat_number || <span className="text-slate-600">—</span>}</span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                <Input defaultValue={lead.first_name} onBlur={(e) => patch({ first_name: e.target.value })} placeholder="Prénom" className="border-slate-700 bg-slate-950/40" />
                <Input defaultValue={lead.last_name} onBlur={(e) => patch({ last_name: e.target.value })} placeholder="Nom" className="border-slate-700 bg-slate-950/40" />
                <Input defaultValue={lead.phone} onBlur={(e) => patch({ phone: e.target.value })} placeholder="Téléphone" className="border-slate-700 bg-slate-950/40" />
                <Input defaultValue={lead.email} onBlur={(e) => patch({ email: e.target.value })} placeholder="Email" className="border-slate-700 bg-slate-950/40" />
                <Input defaultValue={lead.address_street} onBlur={(e) => patch({ address_street: e.target.value })} placeholder="Rue" className="col-span-2 border-slate-700 bg-slate-950/40" />
                <Input defaultValue={lead.address_zip} onBlur={(e) => patch({ address_zip: e.target.value })} placeholder="CP" className="border-slate-700 bg-slate-950/40" />
                <Input defaultValue={lead.address_city} onBlur={(e) => patch({ address_city: e.target.value })} placeholder="Ville" className="border-slate-700 bg-slate-950/40" />
                <Input
                  defaultValue={lead.vat_number}
                  onBlur={(e) => patch({ vat_number: e.target.value.toUpperCase().replace(/[.\s]/g, "") })}
                  placeholder="N° TVA (ex. BE0123456789)"
                  className="col-span-2 border-slate-700 bg-slate-950/40 font-mono"
                  data-testid="lead-vat-number"
                />
                <Input
                  defaultValue={lead.peppol_endpoint_id}
                  onBlur={(e) => patch({ peppol_endpoint_id: e.target.value.trim() })}
                  placeholder="Peppol endpoint (0208:BE0123456789)"
                  className="col-span-2 border-slate-700 bg-slate-950/40 font-mono"
                  data-testid="lead-peppol-endpoint-id"
                />
              </div>
              )}
              <p className="mt-1 text-[10px] text-slate-500">
                Le n° TVA est obligatoire pour l&apos;autoliquidation B2B. L&apos;endpoint Peppol
                est dérivé automatiquement du n° TVA si vide (format <span className="font-mono">0208:BE...</span>).
              </p>
              <div className="mt-3">
                <Label className="text-xs text-slate-500">Description projet</Label>
                <Textarea defaultValue={lead.project_description} onBlur={(e) => patch({ project_description: e.target.value })} rows={3} className="mt-1 border-slate-700 bg-slate-950/40" data-testid="lead-desc" />
              </div>
            </CardContent>
          </Card>

          <TasksBlock leadId={leadId} />

          <LeadTimeline leadId={leadId} />

          <Card className="border-slate-800 bg-slate-900/40">
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200">
                  <MessageCircle className="h-4 w-4" /> Historique
                </h3>
              </div>
              <div className="mb-3 flex gap-2">
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ajouter une note…"
                  rows={2}
                  className="border-slate-700 bg-slate-950/40"
                  data-testid="new-note-input"
                />
                <Button onClick={addNote} disabled={!note.trim()} className="bg-[#C4553B] text-white hover:bg-[#A8402C]" data-testid="add-note-btn">
                  Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {interactions.length === 0 ? (
                  <p className="text-sm text-slate-500">Aucune interaction pour le moment.</p>
                ) : interactions.map((i) => (
                  <div key={i.id} className="rounded border border-slate-800 bg-slate-950/30 p-3 text-sm">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span className="font-medium text-slate-400">{i.type}</span>
                      <span className="tabular">{formatDate(i.created_at)}</span>
                    </div>
                    {i.content && <p className="mt-1 text-slate-200">{i.content}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {(() => {
            const history = Array.isArray(lead.raw_payload_history) && lead.raw_payload_history.length > 0
              ? lead.raw_payload_history
              : (lead.webhook_payload ? [{ received_at: lead.created_at, source_id: lead.source_id, raw_payload: lead.webhook_payload }] : []);
            if (history.length === 0) return null;
            return (
              <Card className="border-slate-800 bg-slate-900/40" data-testid="lead-payload-history">
                <CardContent className="p-4">
                  <button
                    onClick={() => setShowPayload(!showPayload)}
                    className="flex w-full items-center justify-between gap-2 text-sm font-semibold text-slate-200"
                    data-testid="lead-payload-history-toggle"
                  >
                    <span className="inline-flex items-center gap-2">
                      {showPayload ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Historique des payloads
                    </span>
                    <span className="text-xs font-normal text-slate-500">
                      {history.length} {history.length > 1 ? "soumissions" : "soumission"}
                    </span>
                  </button>
                  {showPayload && (
                    <div className="mt-3 space-y-3">
                      {history.map((h, idx) => (
                        <div key={idx} className="rounded border border-slate-800 bg-slate-950/50 p-3" data-testid={`lead-payload-entry-${idx}`}>
                          <div className="mb-2 flex items-center justify-between text-xs text-slate-400">
                            <span className="font-medium text-slate-300">Soumission #{idx + 1}</span>
                            <span className="tabular">{formatDate(h.received_at)}</span>
                          </div>
                          <pre className="max-h-48 overflow-auto rounded bg-slate-900 p-2 text-[11px] text-slate-300">
                            {JSON.stringify(h.raw_payload, null, 2)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </div>

        <div className="space-y-4">
          {/* Phase 2C.M+ (2026-09-06) — Bloc "Devis" en tête de colonne
              droite. Le parcours commercial standard = lead → RDV → DEVIS
              → signature → chantier auto. Positionné AVANT Chantiers car
              le chantier n'existe qu'après signature d'un devis. */}
          <Card className="border-slate-800 bg-slate-900/40" data-testid="devis-section">
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200">
                  <FileText className="h-4 w-4" /> Devis ({quotes.length})
                </h3>
                <button
                  onClick={createQuote}
                  disabled={creatingQuote}
                  className="inline-flex items-center gap-1 rounded-md bg-[#C4553B] px-2 py-1 text-xs font-semibold text-white hover:bg-[#A8402C] disabled:opacity-50"
                  data-testid="new-quote-btn"
                >
                  {creatingQuote ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  Nouveau
                </button>
              </div>
              {quotes.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun devis pour ce projet.</p>
              ) : (
                <ul className="space-y-2">
                  {quotes.map((q) => {
                    const meta = QUOTE_STATUS_LABELS[q.status] || QUOTE_STATUS_LABELS.brouillon;
                    return (
                      <li key={q.id}>
                        <Link
                          to={`/quotes/${q.id}`}
                          className="block rounded border border-slate-800 bg-slate-950/30 p-2 hover:border-[#C4553B]/50 hover:bg-slate-950/50"
                          data-testid={`quote-link-${q.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium text-slate-200">
                                {q.number || (
                                  <span className="italic text-slate-400">{q.title || "Sans titre"}</span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500">{fmtEur(q.total_tvac)}</div>
                            </div>
                            <Badge className={`${meta.cls} shrink-0 border text-[10px]`}>
                              {meta.label}
                            </Badge>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/40">
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200">
                  <Hammer className="h-4 w-4" /> Chantiers ({chantiers.length})
                </h3>
                {/* Phase 2A (2026-09-02) — Bouton "Ajouter" retiré : un seul chemin
                    contact → projet → devis → accepté → chantier auto.
                    Voir DECISIONS.md § "Un seul chemin devis → chantier". */}
              </div>
              {chantiers.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun chantier rattaché.</p>
              ) : (
                <ul className="space-y-2">
                  {chantiers.map(c => (
                    <li key={c.id}>
                      <Link
                        to={`/chantiers/${c.id}`}
                        className="block rounded border border-slate-800 bg-slate-950/30 p-2 hover:border-[#C4553B]/50 hover:bg-slate-950/50"
                        data-testid={`chantier-link-${c.id}`}
                      >
                        <div className="font-medium text-slate-200">{c.name}</div>
                        <div className="text-xs text-slate-500">Statut : {c.status}</div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/40" data-testid="rdv-section">
            <CardContent className="p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-200">Rendez-vous</h3>
                <button
                  onClick={openNewRdv}
                  className="inline-flex items-center gap-1 rounded-md bg-[#C4553B] px-2 py-1 text-xs font-semibold text-white hover:bg-[#A8402C]"
                  data-testid="new-rdv-btn"
                >
                  <Plus className="h-3 w-3" /> Nouveau
                </button>
              </div>
              {rendezvous.filter((r) => r.status !== "cancelled").length === 0 ? (
                <p className="text-sm text-slate-500">Aucun rendez-vous programmé.</p>
              ) : (
                <ul className="space-y-2">
                  {rendezvous
                    .filter((r) => r.status !== "cancelled")
                    .map((r) => (
                      <li
                        key={r.id}
                        className="rounded border border-slate-800 bg-slate-950/30 p-2"
                        data-testid={`rdv-item-${r.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-1 text-sm font-medium text-slate-100">
                              <Calendar className="h-3 w-3 text-[#C4553B]" />
                              {formatDateAndTimeSlot(r.scheduled_at, r.duration_minutes)}
                              <RdvSyncBadge status={r.google_sync_status} className="ml-1" />
                            </div>
                            <div className="text-xs text-slate-500">
                              {r.duration_minutes} min · {r.location || "lieu non précisé"}
                            </div>
                            {r.contact_name && (
                              <div className="text-xs text-slate-400 mt-0.5">
                                Contact : {r.contact_name}
                              </div>
                            )}
                            {r.status === "rescheduled" && (
                              <Badge className="badge-warning mt-1 border text-[10px]">
                                Replanifié
                              </Badge>
                            )}
                          </div>
                          <div className="flex gap-0.5">
                            <button
                              onClick={() => downloadIcs(r)}
                              title="Télécharger .ICS"
                              className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
                              data-testid={`rdv-ics-${r.id}`}
                            >
                              <Download className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => openEditRdv(r)}
                              title="Modifier"
                              className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
                              data-testid={`rdv-edit-${r.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => cancelRdv(r)}
                              title="Annuler"
                              className="rounded p-1 text-slate-400 hover:bg-red-500/10 hover:text-[#E67B6D]"
                              data-testid={`rdv-cancel-${r.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        {/* Phase D2 — Bloc débriefing (mode auto : à débriefer / clôturé / annulé) */}
                        <div className="mt-2">
                          <RdvDebriefBlock
                            rdv={r}
                            leadId={lead.id}
                            chantierId={r.chantier_id}
                            onUpdated={load}
                          />
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={chantierOpen} onOpenChange={setChantierOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle>Nouveau chantier</DialogTitle>
            <DialogDescription className="text-slate-400">
              Rattaché au lead {lead.first_name} {lead.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom du chantier *</Label>
              <Input value={chantierForm.name} onChange={(e) => setChantierForm({ ...chantierForm, name: e.target.value })} placeholder="Toiture 45 rue de la Paix" className="border-slate-700 bg-slate-950/40 mt-1" data-testid="chantier-name" /></div>
            {chantierTypes.length > 0 && (
              <div><Label>Type</Label>
                <Select value={chantierForm.chantier_type_id || "none"} onValueChange={(v) => setChantierForm({ ...chantierForm, chantier_type_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="border-slate-700 bg-slate-950/40 mt-1"><SelectValue placeholder="Optionnel" /></SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900">
                    <SelectItem value="none">— Aucun type —</SelectItem>
                    {chantierTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChantierOpen(false)} className="border-slate-700 bg-slate-800 text-slate-200">Annuler</Button>
            <Button onClick={createChantier} className="bg-[#C4553B] text-white hover:bg-[#A8402C]" data-testid="chantier-submit">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={rdvOpen} onOpenChange={setRdvOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {rdvEditing ? "Modifier le rendez-vous" : "Nouveau rendez-vous"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {lead.first_name} {lead.last_name} — un email de confirmation avec pièce jointe .ICS sera envoyé au client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Date et heure *</Label>
                {/* Split date + time : le picker natif `datetime-local` est
                    inutilisable sur iPad Safari (overlay bloque tous les
                    autres champs). Les inputs `date` / `time` séparés ouvrent
                    des pickers légers et compatibles clavier/mobile. */}
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={splitDateTime(rdvForm.scheduled_at).date}
                    onChange={(e) => setRdvForm({
                      ...rdvForm,
                      scheduled_at: joinDateTime(e.target.value, splitDateTime(rdvForm.scheduled_at).time || "09:00"),
                    })}
                    className="h-10 flex-1 border-slate-700 bg-slate-950/40"
                    data-testid="rdv-scheduled-date"
                  />
                  <TimePresetSelect
                    value={splitDateTime(rdvForm.scheduled_at).time}
                    onChange={(t) => setRdvForm({
                      ...rdvForm,
                      scheduled_at: joinDateTime(splitDateTime(rdvForm.scheduled_at).date, t),
                    })}
                    testid="rdv-scheduled-time"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Durée</Label>
                <Select
                  value={
                    [30, 45, 60, 90, 120].includes(Number(rdvForm.duration_minutes))
                      ? String(rdvForm.duration_minutes)
                      : "custom"
                  }
                  onValueChange={(v) => {
                    if (v === "custom") {
                      // Phase A hotfix Item A : passer en Personnalisé démarre
                      // le champ VIDE (`""`), jamais un placeholder numérique
                      // comme 75 qui ne peut plus être effacé. Le state accepte
                      // string libre pour permettre la saisie/effacement.
                      setRdvForm({ ...rdvForm, duration_minutes: "" });
                    } else {
                      setRdvForm({ ...rdvForm, duration_minutes: Number(v) });
                    }
                  }}
                >
                  <SelectTrigger className="h-10 border-slate-700 bg-slate-950/40" data-testid="rdv-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900">
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">1 h</SelectItem>
                    <SelectItem value="90">1 h 30</SelectItem>
                    <SelectItem value="120">2 h</SelectItem>
                    <SelectItem value="custom">Personnalisé…</SelectItem>
                  </SelectContent>
                </Select>
                {![30, 45, 60, 90, 120].includes(Number(rdvForm.duration_minutes)) && (
                  <Input
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min={15}
                    max={480}
                    step={15}
                    value={
                      rdvForm.duration_minutes === "" || rdvForm.duration_minutes == null
                        ? ""
                        : rdvForm.duration_minutes
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Champ vide → state `""` (permet l'effacement, jamais `0`).
                      // Sinon parse en int. Zéro/négatif seront rejetés HTTP 400
                      // par le backend (validation stricte Phase A).
                      setRdvForm({
                        ...rdvForm,
                        duration_minutes: raw === "" ? "" : Number(raw),
                      });
                    }}
                    className="mt-1.5 border-slate-700 bg-slate-950/40"
                    placeholder="minutes"
                    data-testid="rdv-duration-custom"
                  />
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Contact présent</Label>
              <Input
                value={rdvForm.contact_name}
                onChange={(e) => setRdvForm({ ...rdvForm, contact_name: e.target.value })}
                placeholder="Nom du contact sur place"
                className="border-slate-700 bg-slate-950/40"
                data-testid="rdv-contact-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Lieu</Label>
              <Textarea
                rows={2}
                value={rdvForm.location}
                onChange={(e) => setRdvForm({ ...rdvForm, location: e.target.value })}
                placeholder="Adresse complète du RDV"
                className="border-slate-700 bg-slate-950/40"
                data-testid="rdv-location"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes de préparation</Label>
              <Textarea
                rows={2}
                value={rdvForm.notes}
                onChange={(e) => setRdvForm({ ...rdvForm, notes: e.target.value })}
                placeholder="Ex. Vérifier accès, prévoir échafaudage…"
                className="border-slate-700 bg-slate-950/40"
                data-testid="rdv-notes"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>Rappel avant</Label>
                <Select
                  value={String(rdvForm.reminder_minutes_before)}
                  onValueChange={(v) => setRdvForm({ ...rdvForm, reminder_minutes_before: v })}
                >
                  <SelectTrigger className="border-slate-700 bg-slate-950/40" data-testid="rdv-reminder">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900">
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="60">1 h</SelectItem>
                    <SelectItem value="120">2 h</SelectItem>
                    <SelectItem value="1440">1 jour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Chargé du RDV</Label>
                <Select
                  value={rdvForm.assigned_user_id || "none"}
                  onValueChange={(v) => setRdvForm({ ...rdvForm, assigned_user_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="border-slate-700 bg-slate-950/40" data-testid="rdv-assigned">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900">
                    <SelectItem value="none">Non assigné</SelectItem>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.first_name} {m.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRdvOpen(false)}
              className="border-slate-700 bg-slate-800 text-slate-200"
            >
              Annuler
            </Button>
            <Button
              onClick={submitRdv}
              disabled={savingRdv || !rdvForm.scheduled_at}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="rdv-submit"
            >
              {savingRdv && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {rdvEditing ? "Enregistrer" : "Créer le RDV"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LossReasonModal
        open={showLossModal}
        leadId={leadId}
        leadName={`${lead.first_name || ""} ${lead.last_name || ""}`.trim() || lead.email}
        onDone={(updated) => {
          setShowLossModal(false);
          setData((cur) => (cur ? { ...cur, lead: { ...cur.lead, ...updated } } : cur));
        }}
        onCancel={() => setShowLossModal(false)}
      />

      {/* Modal : afficher / renvoyer le lien du hub client */}
      <Dialog open={hubModal === "view"} onOpenChange={(o) => !o && setHubModal(null)}>
        <DialogContent className="max-w-lg border-slate-800 bg-slate-900 text-slate-100" data-testid="hub-view-modal">
          <DialogHeader>
            <DialogTitle>Lien du hub client</DialogTitle>
            <DialogDescription className="text-slate-400">
              URL personnelle du prospect. Toute personne ayant ce lien peut consulter ses devis et factures.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-500">URL du hub</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  readOnly
                  value={hubInfo?.hub_url || ""}
                  className="font-mono text-xs border-slate-700 bg-slate-950/60"
                  data-testid="hub-url-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!hubInfo?.hub_url) return;
                    try {
                      await navigator.clipboard.writeText(hubInfo.hub_url);
                      setHubCopied(true);
                      setTimeout(() => setHubCopied(false), 2000);
                      toast.success("URL copiée");
                    } catch (_) {
                      toast.error("Copie impossible");
                    }
                  }}
                  className="border-slate-700 bg-slate-800 text-slate-200"
                  data-testid="hub-copy-btn"
                >
                  {hubCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Envoyer le lien par email au prospect</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type="email"
                  value={hubResendTo}
                  onChange={(e) => setHubResendTo(e.target.value)}
                  className="border-slate-700 bg-slate-950/60"
                  placeholder="email@prospect.be"
                  data-testid="hub-resend-input"
                />
                <Button
                  onClick={async () => {
                    if (!hubResendTo) return;
                    setHubBusy(true);
                    try {
                      await api.post(`/leads/${leadId}/hub/resend`, { to: hubResendTo });
                      toast.success(`Lien envoyé à ${hubResendTo}`);
                      setHubModal(null);
                    } catch (e) {
                      toast.error(formatApiError(e));
                    } finally {
                      setHubBusy(false);
                    }
                  }}
                  disabled={hubBusy || !hubResendTo}
                  className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
                  data-testid="hub-resend-submit"
                >
                  {hubBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
                  Envoyer
                </Button>
              </div>
            </div>
            <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
              <div className="flex items-start gap-2">
                <RefreshCw className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <div className="font-semibold">En cas de fuite du lien</div>
                  <p className="mt-1 text-amber-200/80">
                    Régénérez le token — l&apos;ancien URL deviendra immédiatement invalide (404).
                  </p>
                  <Button
                    onClick={() => setHubModal("regenerate")}
                    variant="outline"
                    size="sm"
                    className="mt-2 border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20"
                    data-testid="hub-regenerate-cta"
                  >
                    <RefreshCw className="mr-1 h-3 w-3" /> Régénérer le lien
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={hubModal === "regenerate"} onOpenChange={(o) => !o && setHubModal(null)}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100" data-testid="hub-regenerate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-amber-400" /> Régénérer le lien du hub
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              L&apos;ancien URL deviendra <b className="text-amber-300">immédiatement invalide</b>.
              Confirmez uniquement en cas de fuite ou de partage non souhaité.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHubModal("view")}
              className="border-slate-700 bg-slate-800 text-slate-200"
            >
              Annuler
            </Button>
            <Button
              onClick={async () => {
                setHubBusy(true);
                try {
                  const { data } = await api.post(`/leads/${leadId}/hub/regenerate`);
                  setHubInfo(data);
                  toast.success("Lien du hub régénéré");
                  setHubModal("view");
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setHubBusy(false);
                }
              }}
              disabled={hubBusy}
              className="bg-amber-600 text-white hover:bg-amber-700"
              data-testid="hub-regenerate-confirm"
            >
              {hubBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Régénérer maintenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
